// URL safety helpers (SSRF defence-in-depth + safe links).
//
// RichVault has no server of its own: link previews are fetched by Microlink and
// favicons by Google. These helpers keep private/internal destinations from ever
// being sent to those third parties, re-check the final destination Microlink
// reports after redirects, and stop non-http(s) schemes (javascript:, data:) from
// reaching an <a href>.

const MAX_URL_LENGTH = 2048;
const BLOCKED_SUFFIXES = ['.localhost', '.local', '.internal', '.lan', '.home.arpa', '.intranet', '.corp'];

const isPrivateIPv4 = (host) => {
  const m = host.match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/);
  if (!m) return false;
  const [a, b] = [Number(m[1]), Number(m[2])];
  return (
    a === 0 || a === 10 || a === 127 || a >= 224 ||
    (a === 169 && b === 254) ||
    (a === 172 && b >= 16 && b <= 31) ||
    (a === 192 && b === 168) ||
    (a === 100 && b >= 64 && b <= 127) ||
    (a === 198 && (b === 18 || b === 19))
  );
};

/**
 * Parses user input into a public http(s) URL.
 * @param {string} raw
 * @returns {{ ok: true, url: string } | { ok: false, reason: string }}
 */
export function parsePublicHttpUrl(raw) {
  const input = (raw || '').trim();
  if (!input) return { ok: false, reason: 'empty' };
  if (input.length > MAX_URL_LENGTH) return { ok: false, reason: 'too-long' };

  let parsed;
  try {
    parsed = new URL(/^https?:\/\//i.test(input) ? input : `https://${input}`);
  } catch {
    return { ok: false, reason: 'invalid' };
  }

  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return { ok: false, reason: 'scheme' };
  if (parsed.username || parsed.password) return { ok: false, reason: 'credentials' };

  // URL() already canonicalises decimal/hex/octal IPv4 forms (e.g. 2130706433 -> 127.0.0.1).
  const host = parsed.hostname.toLowerCase().replace(/\.$/, '');
  if (host.startsWith('[')) return { ok: false, reason: 'ip-literal' }; // all IPv6 literals
  if (host === 'localhost' || BLOCKED_SUFFIXES.some(s => host.endsWith(s))) return { ok: false, reason: 'private-host' };
  if (isPrivateIPv4(host)) return { ok: false, reason: 'private-ip' };
  if (!host.includes('.')) return { ok: false, reason: 'private-host' }; // single-label intranet names

  return { ok: true, url: parsed.href };
}

/** href-safe value: the URL if it is http(s) with a host, otherwise undefined. */
export function safeHref(raw) {
  const input = (raw || '').trim();
  if (!input) return undefined;
  try {
    const parsed = new URL(/^https?:\/\//i.test(input) ? input : `https://${input}`);
    return (parsed.protocol === 'http:' || parsed.protocol === 'https:') && parsed.hostname ? parsed.href : undefined;
  } catch {
    return undefined;
  }
}
